import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOrders, type OrderListItem } from '../hooks/useOrders';
import { ChevronRight, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { formatDistanceToNow, parseISO, differenceInCalendarDays } from 'date-fns';
import { safeFormatDate } from '../lib/utils';
import { StatusBadge } from '../components/StatusBadge';
import type { SalesOrderStatus } from 'scheduler-types';

/**
 * Returns the risk level for an order based on expected vs promised ship date.
 * - 'red': expected is after promised (late)
 * - 'yellow': expected is within 2 days of promised (tight)
 * - null: on track
 */
function getOrderRiskLevel(order: OrderListItem): 'red' | 'yellow' | null {
  if (!order.expectedShipDate || !order.earliestPromisedShipDate) return null;
  try {
    const expected = parseISO(order.expectedShipDate);
    const promised = parseISO(order.earliestPromisedShipDate);
    const daysRemaining = differenceInCalendarDays(promised, expected);
    if (daysRemaining < 0) return 'red';
    if (daysRemaining <= 2) return 'yellow';
    return null;
  } catch {
    return null;
  }
}

const riskRowStyles = {
  red: 'bg-red-50/60 border-l-4 border-l-red-400',
  yellow: 'bg-amber-50/60 border-l-4 border-l-amber-400',
} as const;

export function OrdersPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<SalesOrderStatus | undefined>(
    'active'
  );
  const { data, isLoading, error, refetch, isFetching } = useOrders({
    status: statusFilter,
    limit: 50,
  });

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Orders</h1>
          <p className="text-sm text-foreground-muted mt-1">
            View order status and delivery schedules
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-background-muted transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-foreground">Status:</label>
        <select
          value={statusFilter ?? 'all'}
          onChange={(e) =>
            setStatusFilter(
              e.target.value === 'all' ? undefined : (e.target.value as SalesOrderStatus)
            )
          }
          className="rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="complete">Complete</option>
          <option value="cancelled">Cancelled</option>
        </select>
        {data?.pagination && (
          <span className="text-sm text-foreground-muted">
            Showing {data.data.length} of {data.pagination.total} orders
          </span>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-danger bg-danger-subtle p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-danger" />
            <div>
              <h3 className="font-medium text-danger-foreground">
                Failed to load orders
              </h3>
              <p className="text-sm text-danger-foreground mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 text-sm font-medium text-danger hover:underline"
          >
            Try again
          </button>
        </div>
      ) : data?.data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="h-12 w-12 rounded-full bg-background-muted flex items-center justify-center mb-4">
            <ClipboardIcon className="h-6 w-6 text-foreground-muted" />
          </div>
          <h3 className="text-lg font-medium text-foreground">No orders found</h3>
          <p className="text-sm text-foreground-muted mt-1">
            {statusFilter
              ? `No ${statusFilter} orders to display`
              : 'No orders to display'}
          </p>
        </div>
      ) : (
        <div className="bg-background rounded-lg border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background-subtle">
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Order #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Client
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Items
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  At Risk
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Promised Ship
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Expected Ship
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-4 py-3 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data?.data.map((order) => {
                const risk = getOrderRiskLevel(order);
                return (
                <tr
                  key={order.id}
                  className={`hover:bg-background-subtle/80 transition-colors cursor-pointer ${
                    risk ? riskRowStyles[risk] : ''
                  }`}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm font-medium text-foreground">
                      {order.gpSalesOrderNumber}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-foreground">
                      {order.customerName}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground-muted">
                    {order.lineItemCount} item{order.lineItemCount !== 1 ? 's' : ''}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {order.lineItemsAtRisk > 0 ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">
                        {order.lineItemsAtRisk}
                      </span>
                    ) : (
                      <span className="text-foreground-muted">0</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {order.earliestPromisedShipDate ? (
                      <span className="text-foreground">
                        {safeFormatDate(order.earliestPromisedShipDate, 'MMM d, yyyy')}
                      </span>
                    ) : (
                      <span className="text-foreground-muted">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {order.expectedShipDate ? (
                      <div>
                        <div className="text-foreground font-medium">
                          {safeFormatDate(order.expectedShipDate, 'MMM d, yyyy')}
                        </div>
                        {order.expectedShipDateWorst && order.expectedShipDateWorst !== order.expectedShipDate && (
                          <div className="text-xs text-foreground-muted">
                            worst: {safeFormatDate(order.expectedShipDateWorst, 'MMM d')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-foreground-muted">Not scheduled</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground-muted">
                    {order.updatedAt
                      ? formatDistanceToNow(new Date(order.updatedAt), {
                          addSuffix: true,
                        })
                      : '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <Link
                      to={`/orders/${order.id}`}
                      className="inline-flex items-center text-sm font-medium text-primary hover:text-primary-hover"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Simple clipboard icon for empty state
function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
      />
    </svg>
  );
}
